import { IAccountingBasisExtendedDTO } from '@modeso/types__aumico-lib-settings-be';
import { createEntityAdapter, Dictionary, EntityAdapter } from '@ngrx/entity';
import { Action, createReducer, createSelector, MemoizedSelector, on } from '@ngrx/store';
import * as settingsActions from '../actions/settings.actions';
import { IAccountingBasisState } from '../interfaces/accounting-basis-state.interface';

function selectAccountingBasisId(a: IAccountingBasisExtendedDTO): string {
    return a._id;
}

function sortByName(a: IAccountingBasisExtendedDTO, b: IAccountingBasisExtendedDTO): number {
    return a.name.localeCompare(b.name);
}

const adapter: EntityAdapter<IAccountingBasisExtendedDTO> = createEntityAdapter<IAccountingBasisExtendedDTO>({
    selectId: selectAccountingBasisId,
    sortComparer: sortByName,
});

const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

const selectEntity = createSelector(
    selectEntities,
    (entities: Dictionary<IAccountingBasisExtendedDTO>, props: { id: string }) => entities[props.id],
);

export const initialAccountingBasisState: IAccountingBasisState = adapter.getInitialState({
    // additional entity state properties
    // activeVariable: null,
});

export function createSelectors<T>(accountingBasisStateSelector: MemoizedSelector<T, IAccountingBasisState>) {
    return {
        selectAll: createSelector(accountingBasisStateSelector, selectAll),
        selectEntities: createSelector(accountingBasisStateSelector, selectEntities),
        selectActive: createSelector(accountingBasisStateSelector, (state) => state.activeAccountingBasis),
        selectError: createSelector(accountingBasisStateSelector, (state) => state.error),
    };
}

const privateAccountingBasesReducer = createReducer(
    initialAccountingBasisState,
    on(settingsActions.accountingBasisActions.onGetAllAccountingBasisSuccess, (state, action) =>
        adapter.setAll(action.payload, { ...state, error: undefined }),
    ),
    on(settingsActions.accountingBasisActions.onGetAllAccountingBasisFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),
    on(settingsActions.accountingBasisActions.setActiveAccountingBasis, (state, action) => ({
        ...state,
        error: undefined,
        activeAccountingBasis:
            typeof action.payload === 'string' ? selectEntity(state, { id: action.payload }) : action.payload,
    })),

    on(settingsActions.accountingBasisActions.onSubmitCreateAccountingBasisSuccess, (state, action) => ({
        ...state,
        error: undefined,
        activeAccountingBasis: null,
    })),
    on(settingsActions.accountingBasisActions.onSubmitCreateAccountingBasisFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),
    on(settingsActions.accountingBasisActions.onSubmitEditAccountingBasisSuccess, (state, action) => ({
        ...state,
        error: undefined,
        activeAccountingBasis: null,
    })),
    on(settingsActions.accountingBasisActions.onSubmitEditAccountingBasisFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),
);

export function accountingBasesReducer(state: IAccountingBasisState | undefined, action: Action) {
    return privateAccountingBasesReducer(state, action);
}
